'use client'

import React from 'react'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined'
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined'
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'

import type { HolidayBulletListSection } from '@/src/types/holidays'

import DocumentChecklist, { type DocumentItem } from './documentChecklist'

interface DocumentChecklistWrapperProps {
    section: HolidayBulletListSection
    isMobile: boolean
}

/**
 * Keyword to icon. Ordered most specific first — "residence visa" must beat the bare "visa" that
 * appears in almost every line. The item's own wording is never altered; the icon only makes the
 * list scannable.
 */
const ICON_RULES: Array<[RegExp, DocumentItem['Icon']]> = [
    [/birth cert/i, ChildCareOutlinedIcon],
    [/photograph|photo\b|picture/i, PhotoCameraOutlinedIcon],
    [/bank statement|financial|means of subsistence|salary/i, AccountBalanceOutlinedIcon],
    [/insurance/i, HealthAndSafetyOutlinedIcon],
    [/noc|no objection|employer|sponsor|trade licen|company letter/i, WorkOutlineIcon],
    [/hotel|accommodation|voucher/i, HotelOutlinedIcon],
    [/flight|ticket|itinerary/i, FlightOutlinedIcon],
    [/residence visa|resident permit|residence permit|emirates id/i, BadgeOutlinedIcon],
    [/passport/i, BadgeOutlinedIcon],
    [/application form|form\b/i, ArticleOutlinedIcon]
]

/**
 * The earliest keyword wins, with rule order breaking ties. Upstream sometimes runs two documents
 * into one line ("Passport, clear scan copy... 2. Hotel details."), and the icon should name what
 * the line leads with rather than whatever term happens to sit highest in the rules.
 */
const iconFor = (text: string): DocumentItem['Icon'] => {
    let best: DocumentItem['Icon'] = DescriptionOutlinedIcon
    let bestAt = Infinity

    ICON_RULES.forEach(([pattern, Icon]) => {
        const match = pattern.exec(text)
        if (match && match.index < bestAt) {
            best = Icon
            bestAt = match.index
        }
    })

    return best
}

const DocumentChecklistWrapper: React.FC<DocumentChecklistWrapperProps> = ({ section, isMobile }) => {
    const items: DocumentItem[] = section.items
        .map(text => text.trim())
        .filter(Boolean)
        .map(text => ({ text, Icon: iconFor(text) }))

    if (!items.length) return null

    return <DocumentChecklist items={items} footnotes={section.footnotes ?? []} isMobile={isMobile} />
}

export { DocumentChecklistWrapper }
export default DocumentChecklistWrapper
